/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { SelectChangeEvent } from '@mui/material';
import { MenuItem } from '@mui/material';
import type { TFunction } from 'i18next';
import React from 'react';
import { TooltipSelect } from '@opengeoweb/shared';
import { REFERENCE_TIME_ID } from '../../../constants';
import type { CustomDimension, Parameter } from '../../TimeSeries/types';

const ParameterDimensionIdSelect: React.FC<{
  rowIsEnabled: boolean;
  parameter: Parameter;
  customDimensions: CustomDimension[];
  hasReferenceTime: boolean;
  patchParameter: (parameter: Partial<Parameter>) => void;
  selectedDimensionId?: string;
  setSelectedDimensionId: (dimensionId: string) => void;
  t: TFunction;
}> = ({
  rowIsEnabled,
  parameter,
  customDimensions,
  hasReferenceTime,
  patchParameter,
  selectedDimensionId,
  setSelectedDimensionId,
  t,
}) => {
  // Custom dimensions or reference time are not available
  if (
    (!customDimensions || customDimensions.length === 0) &&
    !hasReferenceTime
  ) {
    return null;
  }

  const defaultDimension = hasReferenceTime
    ? REFERENCE_TIME_ID
    : customDimensions[0].id;

  const activeDimensionId = selectedDimensionId || defaultDimension;

  return (
    <TooltipSelect
      SelectDisplayProps={{ 'aria-label': 'timeseriesManager-dimensionId' }}
      data-testid="parameterDimensioIdSelect"
      isEnabled={rowIsEnabled}
      onChange={(event: SelectChangeEvent<unknown>): void => {
        const newDimensionId = event.target.value as string;
        // Restore any previously entered value for this dimension id instead of resetting it
        const existingValue =
          parameter.dimensions?.find((dim) => dim.id === newDimensionId)
            ?.value ?? '';
        setSelectedDimensionId(newDimensionId);
        patchParameter({
          id: parameter.id,
          dimensions: [{ id: newDimensionId, value: existingValue }],
        });
      }}
      tooltip={`${t('timeseries-dimension-id')}: ${activeDimensionId}`}
      // If no dimension id selected yet, choose reference time if it is available, otherwise the first dimension
      value={activeDimensionId}
    >
      <MenuItem disabled>{t('timeseries-dimension-id')}</MenuItem>
      {!!hasReferenceTime && (
        <MenuItem
          data-testid="timeseries-dimension-id-reference-time"
          key="dimensionmenu-id-reference-time"
          value={REFERENCE_TIME_ID}
        >
          {t('timeseries-reference-time')}
        </MenuItem>
      )}
      {customDimensions?.map((dimension: CustomDimension) => {
        return (
          <MenuItem
            data-testid={`timeseries-dimension-id-${dimension.id}`}
            key={`dimensionmenu-id-${dimension.id}`}
            value={dimension.id}
          >
            {dimension.id}
          </MenuItem>
        );
      })}
    </TooltipSelect>
  );
};

export default ParameterDimensionIdSelect;
